import React from 'react';
import { motion } from 'framer-motion';
import { LuArrowRight, LuCalendar, LuHeart } from 'react-icons/lu';

// ExploreComparisonCard — the EXPLORE surface's main BEFORE/AFTER card
// (design-recreation mockup, desktop lines 689-875 + mobile 1313-1445).
//
// Honest-data notes:
//   - The deck card has NO vote counts or scores, so the identity block reads
//     ANONYMOUS / ENCOURAGEMENT ONLY instead of "{votes} VOTES", and the
//     portraits carry no score bars (the anonymization already censors
//     identity).
//   - Date chip shows time_gap_label ("MONTHS APART") or, missing that, the
//     after-portrait date.
//
// One DOM instance serves both breakpoints:
//   desktop (lg+): identity block + date chip header, captions above the
//                  3:4 portraits, center arrow medallion.
//   mobile (<lg):  "Anonymous · {gap} glowup" meta row, "{GAP} / GLOWUP"
//                  headline, labels overlaid on a bottom gradient.

const MONTHS_SHORT = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

export const formatCapsDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${MONTHS_SHORT[d.getUTCMonth()]} ${String(d.getUTCDate()).padStart(2, '0')}, ${d.getUTCFullYear()}`;
};

export const formatMonthYear = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

export const timeGapChipText = (card) =>
  card.time_gap_label
    ? `${card.time_gap_label} APART`
    : formatCapsDate(card.after && card.after.taken_at);

// Gold heart medallion + ANONYMOUS / ENCOURAGEMENT ONLY. Shared with the
// next-card teaser.
export const IdentityBlock = ({ label = 'ANONYMOUS', caption = 'ENCOURAGEMENT ONLY' }) => (
  <div className="flex items-center gap-[13px]">
    <span
      className="w-[42px] h-[42px] rounded-full bg-vein border border-aurum/40 flex items-center justify-center shrink-0"
      aria-hidden="true"
    >
      <LuHeart size={20} className="text-aurum" fill="currentColor" />
    </span>
    <div>
      <div className="font-display text-bone leading-none" style={{ fontSize: 22 }}>
        {label}
      </div>
      <div
        className="font-display uppercase tracking-[0.18em] text-bone-dim mt-[3px]"
        style={{ fontSize: 11 }}
      >
        {caption}
      </div>
    </div>
  </div>
);

// Rounded pill date chip. Shared with the next-card teaser.
export const DateChip = ({ children }) => (
  <span className="inline-flex items-center gap-2 bg-[#242423] border border-hairline-soft rounded-full px-[15px] py-2">
    <LuCalendar size={14} className="text-bone-dim shrink-0" aria-hidden="true" />
    <span className="font-display uppercase text-[#D8D2C6]" style={{ fontSize: 13, letterSpacing: '0.08em' }}>
      {children}
    </span>
  </span>
);

const PortraitSide = ({ side, label, imageUrl, takenAt }) => (
  <div className="min-w-0">
    {/* desktop caption above the portrait */}
    <div
      className="hidden lg:block text-center font-display uppercase tracking-[0.2em] text-bone-dim mb-2.5"
      style={{ fontSize: 11 }}
    >
      {label} — {formatCapsDate(takenAt)}
    </div>
    <div className="relative overflow-hidden lg:rounded-thumb lg:border lg:border-hairline-soft">
      <img
        src={imageUrl}
        alt={side}
        className="w-full aspect-[3/4] object-cover block"
      />
      {/* mobile overlay: bottom gradient + label/date */}
      <div
        className="lg:hidden absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, transparent 62%, rgba(0,0,0,0.85))' }}
        aria-hidden="true"
      />
      <div className="lg:hidden absolute left-[13px] bottom-3">
        <div className="font-display uppercase tracking-[0.2em] text-bone-pure" style={{ fontSize: 12 }}>
          {label}
        </div>
        <div
          className="font-display uppercase tracking-[0.14em] text-[#B8B2A6] mt-0.5"
          style={{ fontSize: 12 }}
        >
          {formatMonthYear(takenAt)}
        </div>
      </div>
    </div>
  </div>
);

const ExploreComparisonCard = ({ card }) => {
  const gapChip = timeGapChipText(card);
  const gapLabel = card.time_gap_label || '';

  return (
    <motion.article
      key={card.glowup_id}
      className="bg-[#1B1B1A] border border-hairline-soft rounded-card p-[18px] lg:p-6"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* desktop header: identity block + date chip */}
      <header className="hidden lg:flex justify-between items-center gap-4">
        <IdentityBlock />
        <DateChip>{gapChip}</DateChip>
      </header>

      {/* mobile header: meta row + headline */}
      <header className="lg:hidden">
        <p className="font-display text-[#B8B2A6] m-0" style={{ fontSize: 15 }}>
          {gapLabel
            ? `Anonymous · ${gapLabel.toLowerCase()} glowup`
            : 'Anonymous glowup'}
        </p>
        <div className="mt-3.5">
          {gapLabel && (
            <div
              className="font-display uppercase tracking-[0.22em] text-[#D8D2C6]"
              style={{ fontSize: 13 }}
            >
              {gapLabel}
            </div>
          )}
          <div
            className="font-display text-bone tracking-[0.06em]"
            style={{ fontSize: 38, lineHeight: 1.1 }}
          >
            GLOWUP
          </div>
        </div>
      </header>

      {/* before / after portraits */}
      <div className="relative grid grid-cols-2 gap-0.5 lg:gap-[18px] mt-4 lg:mt-[22px] rounded-field overflow-hidden lg:rounded-none lg:overflow-visible">
        <PortraitSide
          side="before"
          label="BEFORE"
          imageUrl={card.before && card.before.anonymized_image_url}
          takenAt={card.before && card.before.taken_at}
        />
        <PortraitSide
          side="after"
          label="AFTER"
          imageUrl={card.after && card.after.anonymized_image_url}
          takenAt={card.after && card.after.taken_at}
        />
        {/* center arrow medallion (desktop) */}
        <div
          className="hidden lg:flex absolute left-1/2 top-[48%] -translate-x-1/2 -translate-y-1/2 w-[46px] h-[46px] rounded-full bg-vein border border-hairline items-center justify-center shadow-[0_8px_20px_rgba(0,0,0,0.6)]"
          aria-hidden="true"
        >
          <LuArrowRight size={18} className="text-bone-pure" />
        </div>
      </div>
    </motion.article>
  );
};

export default ExploreComparisonCard;
